import React from 'react';
import { cn } from '@/lib/utils';

interface LabeledToggleProps {
  leftLabel: string;
  rightLabel: string;
  rightSelected: boolean;
  onChange: (rightSelected: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}

const labeledToggleOptionClass =
  'font-heading text-[12px] sm:text-[13px] font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer transition-colors duration-150 px-1 py-0.5 touch-target-pseudo ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background rounded-[0.25rem]';

export const LabeledToggle: React.FC<LabeledToggleProps> = ({
  leftLabel,
  rightLabel,
  rightSelected,
  onChange,
  ariaLabel,
  disabled = false,
}) => {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex items-center gap-2', disabled && 'pointer-events-none opacity-55')}
    >
      <button
        type="button"
        role="radio"
        aria-checked={!rightSelected}
        onClick={() => onChange(false)}
        disabled={disabled}
        className={cn(
          labeledToggleOptionClass,
          !rightSelected ? 'text-primary-text' : 'text-foreground/70 hover:text-foreground',
        )}
      >
        {leftLabel}
      </button>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => onChange(!rightSelected)}
        disabled={disabled}
        className="relative h-6 w-12 rounded-[0.25rem] border border-border/60 bg-surface-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.35)] shrink-0 cursor-pointer touch-target-pseudo"
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-5 w-5 rounded-[2px] bg-[var(--color-primary-hover)] shadow-[0_0_4px_var(--color-primary-hover)] transition-transform duration-200 ease-out',
            rightSelected ? 'translate-x-6' : 'translate-x-0',
          )}
        />
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={rightSelected}
        onClick={() => onChange(true)}
        disabled={disabled}
        className={cn(
          labeledToggleOptionClass,
          rightSelected ? 'text-primary-text' : 'text-foreground/70 hover:text-foreground',
        )}
      >
        {rightLabel}
      </button>
    </div>
  );
};
